import { createSelector, createSlice, current, PayloadAction, UnknownAction } from '@reduxjs/toolkit';
import { Currency } from '@agoda/layout';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { getBootstrapConfig, setBootstrapConfig as setBootstrapConfigInWindow } from 'core/window/windowService';
import { RootState } from 'store/store.types';
import { Weekday } from 'core-framework/util/datetime/Weekday';
import { setCurrencyCookie } from 'core-framework/cookie/CookieManager';

import { goBack, setPageType } from 'store/slice/navigation';
import { getPreviousPageType } from 'core/messaging/analytics/pages/pageTypeUtils';
import { LoyaltySearchType } from 'core/type/LoyaltySearch.types';
import { setLoyaltySearchTypeToLocalStorage } from 'core/utils/loyaltySearchType';
import { TopDestination } from 'core/type/ActivitiesTopDestinations.types';
import { appendQueryStringToCityUrl } from 'core/utils/activityUrlGenerator';
import { getActivityIdQueryString } from 'core/utils/queryString/queryStringUtils';
import { isB } from 'core/window/bootstrapConfig';
import { FunnelExperimentId } from 'core/type/ExperimentId';

export interface BootstrapState {
    config: BootstrapConfig;
    firstDayOfWeek: Weekday;
    loyaltySearchType: LoyaltySearchType;
}

let bootstrapInitialStateLazy: BootstrapState;

const bootstrap = createSlice({
    name: 'bootstrap',
    initialState: () => {
        // Intentional: Somehow webpack is unable to load variable DefaultApiBaseState in this file. This implementation is a lazy initialization implementation.
        if (!bootstrapInitialStateLazy) {
            bootstrapInitialStateLazy = {
                config: getBootstrapConfig(),
                firstDayOfWeek: getBootstrapConfig().behavior.common.isSundayFirstDayOfWeek ? Weekday.Sunday : Weekday.Monday,
                loyaltySearchType: LoyaltySearchType.UNKNOWN,
            };
        }
        return bootstrapInitialStateLazy;
    },
    reducers: {
        setBootstrapConfig: (state, action: PayloadAction<BootstrapConfig>) => {
            state.config = action.payload;
        },
        setCurrency: (state, action: PayloadAction<Currency>) => {
            // @ts-ignore
            state.config.currency = action.payload;
            setCurrencyCookie(state.config.currency.code, state.config.currency.id);
        },
        setLoyaltySearchType: (state, action: PayloadAction<LoyaltySearchType>) => {
            const loyaltySearchType = action.payload;
            state.loyaltySearchType = loyaltySearchType;
            setLoyaltySearchTypeToLocalStorage(loyaltySearchType);
        },
        setCid: (state, action: PayloadAction<number>) => {
            state.config.pageConfig.cid = action.payload;
        },
        setWebViewFeatureToggle: (state, action: PayloadAction<string[]>) => {
            state.config.webViewFeatureToggles = action.payload;
        },
        setShowHeader: (state, action: PayloadAction<boolean>) => {
            state.config.behavior.header.show = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Remove this method when ACT-4968 is integrated. The pageTypeId must be used from BFF.
        builder.addCase(setPageType, (state, action) => {
            const isAct4968 = isB(FunnelExperimentId.ACT_4968, state.config.experiments);
            if (isAct4968) {
                return;
            }
            state.config.pageConfig.pageTypeId = action.payload.pageTypeId;
        });
        // Remove this method when ACT-4968 is integrated
        builder.addCase(goBack, (state, action) => {
            const isAct4968 = isB(FunnelExperimentId.ACT_4968, state.config.experiments);
            if (isAct4968) {
                return;
            }
            state.config.pageConfig.pageTypeId = getPreviousPageType(state.config.browser, state.config.pageConfig.pageTypeId);
        });
        builder.addMatcher(isBootstrapUpdateAction, (state, action) => {
            setBootstrapConfigInWindow(current(state.config));
        });
    },
});

function isBootstrapUpdateAction(action: UnknownAction) {
    return action.type === bootstrap.actions.setCurrency.type || action.type === setPageType.type || action.type === goBack.type || action.type === bootstrap.actions.setCid.type;
}

export const selectBootstrapState = (state: RootState) => state.bootstrap;
export const selectFirstDayOfWeekFromBootstrap = createSelector(selectBootstrapState, (state) => state.firstDayOfWeek);
export const selectConfigFromBootstrap = createSelector(selectBootstrapState, (state) => state.config);

export const selectBehaviorFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.behavior);
export const selectFeatureExperimentsFromBootstrap = createSelector(selectBehaviorFromBootstrap, (behavior) => behavior.featureExperiments);
export const selectFeatureBehaviorFromBootstrap = createSelector(selectBehaviorFromBootstrap, (behavior) => behavior.featureExperiments.featureBehavior);
export const selectCurrencyFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.currency);
export const selectCmsFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.cms);
export const selectCurrencyCodeFromBootstrap = createSelector(selectCurrencyFromBootstrap, (currency) => currency.code);
export const selectExperimentsFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.experiments);

export const selectLanguageFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.language);
export const selectLocaleFromBootstrap = createSelector(selectLanguageFromBootstrap, (language) => language.locale);
export const selectLanguageIdFromBootstrap = createSelector(selectLanguageFromBootstrap, (language) => language.languageId);
export const selectRtlFromBootstrap = createSelector(selectLanguageFromBootstrap, (language) => language.isRtl);
export const selectRequestProfileFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.requestProfile);

export const selectPageConfigFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.pageConfig);
export const selectCidFromBootstrap = createSelector(selectPageConfigFromBootstrap, (pageConfig) => pageConfig.cid);
export const selectPageTypeIdFromBootstrap = createSelector(selectPageConfigFromBootstrap, (pageConfig) => pageConfig.pageTypeId);
export const selectIsWebViewFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.isWebView);
export const selectWhitelabelConfigFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.whiteLabelConfig);
export const selectBrowserInfoFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => ({
    isTablet: config.browser.isTablet,
    isMobile: config.browser.isMobile,
    isDesktop: config.browser.isDesktop,
    isWebView: config.browser.isWebView,
}));
export const selectMobileDeviceTypeFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.userDevice.mobileDeviceType);
export const selectPrerenderStateFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.isPrerender);
export const selectGeoFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.geo);
export const selectActivityIdFromBootstrap = createSelector(selectGeoFromBootstrap, selectConfigFromBootstrap, (geo, config) => {
    return !config.isMockData ? geo?.activityId || 0 : getActivityIdQueryString().activityIdFromUrl || 0;
});
export const selectTopDestinationFromBootstrap = createSelector(selectConfigFromBootstrap, (config): TopDestination[] => {
    const topDestinations = config.geo?.topDestinations || [];
    return topDestinations.map((destination) => {
        return {
            ...destination,
            cityUrl: appendQueryStringToCityUrl(destination.cityUrl, config),
        };
    });
});
export const selectTopDestinationCityIdFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.geo?.topDestinationCityId || 0);
export const selectMemberIdFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.memberId);
export const selectUserIdFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.userId);
export const selectSessionId = createSelector(selectConfigFromBootstrap, (config) => config.sessionId);
export const selectIsLoggedIn = createSelector(selectConfigFromBootstrap, (config) => config.isLoggedIn);
export const selectWebViewFeatureToggles = createSelector(selectConfigFromBootstrap, (config) => config.webViewFeatureToggles ?? []);

export const { setCurrency, setBootstrapConfig, setLoyaltySearchType, setCid, setWebViewFeatureToggle, setShowHeader } = bootstrap.actions;
export default bootstrap.reducer;
export const selectLoyaltySearchTypeFromBootstrapState = createSelector(selectBootstrapState, (state) => state.loyaltySearchType);
export const selectFooterBehavior = createSelector(selectBehaviorFromBootstrap, (behavior) => behavior?.footer);
export const selectUnlockTripSavingsBehavior = createSelector(selectBehaviorFromBootstrap, (behavior) => behavior?.tripSavings?.unlockTripSavings);

export const selectTopSubcategoriesFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.topSubcategories);
export const selectIsBotFromBootstrap = createSelector(selectConfigFromBootstrap, (config) => config.isBot);
export const selectIsBotOrIsPrerenderFromBootstrap = createSelector(
    selectConfigFromBootstrap,
    selectBehaviorFromBootstrap,
    (config, behavior) => behavior.featureExperiments.featureBehavior.ExpandSectionsForBots && (config.isBot || config.isPrerender),
);
